import { useCallback, useEffect, useMemo, useRef } from "react";

/**
 * Only the newest answer may reach the screen.
 *
 * Changing a filter twice fires two requests, and the first can come back last - it then
 * paints the old period's numbers under the new filter, with nothing to tell anyone. Every
 * fetch takes a token from begin() and drops its reply unless isLatest(token) still holds.
 * Leaving the page counts as newer too, so a late reply never sets state on an unmounted
 * screen.
 *
 *   const req = useLatestRequest();
 *   const token = req.begin();
 *   API(x).post(body, (res) => { if (!req.isLatest(token)) return; ... });
 *
 * One component can hold several (the main report and a drill-down each take their own),
 * since a drill-down must not cancel the report behind it.
 */
const useLatestRequest = () => {
  const latest = useRef(0);

  useEffect(
    () => () => {
      latest.current += 1;
    },
    []
  );

  const begin = useCallback(() => {
    latest.current += 1;
    return latest.current;
  }, []);

  const isLatest = useCallback((token) => token === latest.current, []);

  return useMemo(() => ({ begin, isLatest }), [begin, isLatest]);
};

export default useLatestRequest;
